import { FolderIcon } from "../atoms/FolderIcon";
import { DocumentCheckbox } from "../molecules/DocumentCheckbox";
import { DocumentActions } from "../molecules/DocumentActions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { isDocumentProcessing } from "../../utils/documentState";
import { rowSelectionToggle } from "../../utils/rowSelectionClick";
import { type DocumentColumnFlags, documentGridTemplate } from "../../utils/fitDocumentColumns";
import { DocumentDateCell } from "../atoms/DocumentDateCell";
import { DocumentUploaderCell } from "../atoms/DocumentUploaderCell";

interface ContainerFolderRowProps {
  /** Container filename (e.g., "archive.zip") */
  filename: string;
  /** Number of child documents inside the container */
  childCount?: number;
  /** Document processing state */
  state: string | null;
  /** The archive's own dates and uploader, for the date and Uploader columns. */
  document?: {
    created_at?: string | null;
    original_created_at?: string | null;
    document_content_modified_date?: string | null;
    created_by?: string | number | null;
    created_by_name?: string | null;
    created_by_avatar_url?: string | null;
    created_by_color?: string | null;
  };
  /**
   * The list's visible columns, as `SignedContainerRow` takes them: the row
   * sits on the document rows' grid, and the columns an archive has nothing
   * for stay empty.
   */
  columns?: Partial<Omit<DocumentColumnFlags, "showCheckbox" | "showActions">>;
  /** The Uploader column was asked for but did not fit: name them in the date's tooltip. */
  uploaderInDateHint?: boolean;
  /** Whether the row is selected */
  isSelected?: boolean;
  /** Show/hide Checkbox column */
  showCheckbox?: boolean;
  /** Show/hide Actions column */
  showActions?: boolean;
  /** Callback when checkbox is toggled */
  onSelect?: (isSelected: boolean) => void;
  /** Callback when the container row is clicked (navigate into) */
  onClick?: () => void;
  /** Callback when download is clicked */
  onDownload?: () => void;
  /** Callback when delete is clicked */
  onDelete?: () => void;
  /** Custom delete label */
  deleteLabel?: string;
  /** Render the destructive action as a non-destructive Restore action. Used in trash view. */
  isRestore?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ContainerFolderRow - Folder-like row for container files (.zip, .asice).
 *
 * Renders container documents as navigable folder rows with child count,
 * download/delete actions, and processing indicator.
 */
export function ContainerFolderRow({
  filename,
  childCount,
  state,
  document,
  columns = {},
  uploaderInDateHint = false,
  isSelected = false,
  showCheckbox = true,
  showActions = true,
  onSelect,
  onClick,
  onDownload,
  onDelete,
  deleteLabel,
  isRestore = false,
  className,
}: ContainerFolderRowProps) {
  const { t } = useLanguage();
  const isProcessing = state !== null && isDocumentProcessing(state);

  // The document rows' grid, so checkboxes, dates and actions line up.
  const flags: DocumentColumnFlags = {
    showCheckbox,
    showActions,
    showDateCreated: !!columns.showDateCreated,
    showUser: !!columns.showUser,
    showVersion: !!columns.showVersion,
    showStatus: !!columns.showStatus,
    showTags: !!columns.showTags,
    showParent: !!columns.showParent,
  };
  const gridColumns = documentGridTemplate(flags);

  // Plain row click (anywhere except title, checkbox, or actions) toggles selection.
  // Cmd/Ctrl-click also toggles selection (kept for muscle memory).
  // Click on the title navigates into the container.
  const handleRowClick = rowSelectionToggle(isSelected, onSelect);

  const handleTitleClick = (e: React.MouseEvent) => {
    if (!onClick) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.stopPropagation();
    onClick();
  };

  return (
    <div
      className={cn(
        "group grid items-center gap-4 border-b px-6 py-1.5 transition-colors",
        isSelected ? "border-primary/50 bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/50",
        className
      )}
      style={{ gridTemplateColumns: gridColumns }}
      onClick={handleRowClick}
    >
      {/* Checkbox */}
      {showCheckbox && (
        <DocumentCheckbox isSelected={isSelected} onSelect={(selected) => onSelect?.(selected)} />
      )}

      {/* Folder icon, filename and child count, on one line like every row */}
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex flex-shrink-0">
          <FolderIcon isOpen={false} className="h-5 w-5" />
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <button
            type="button"
            data-row-title
            onClick={handleTitleClick}
            disabled={!onClick}
            className={cn(
              "min-w-0 max-w-full truncate text-left text-sm font-medium",
              onClick && "cursor-pointer hover:text-primary hover:underline"
            )}
          >
            {filename}
          </button>
          {(isProcessing || childCount !== undefined) && (
            <span className="flex min-w-0 flex-shrink-0 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
              {isProcessing ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t.documents.signedContainer.extracting}
                </>
              ) : (
                <>
                  {childCount === 1
                    ? t.documents.folder.documentCountOne
                    : t.documents.folder.documentCount.replace("{count}", String(childCount))}
                </>
              )}
            </span>
          )}
        </div>
      </div>

      {flags.showDateCreated && (
        <DocumentDateCell
          document={document ?? {}}
          uploaderName={uploaderInDateHint ? document?.created_by_name : null}
        />
      )}
      {flags.showUser && (
        <DocumentUploaderCell
          createdBy={document?.created_by}
          createdByName={document?.created_by_name}
          createdByAvatarUrl={document?.created_by_avatar_url}
          createdByColor={document?.created_by_color}
        />
      )}
      {flags.showVersion && <div />}
      {flags.showStatus && <div />}
      {flags.showTags && <div />}
      {flags.showParent && <div />}

      {/* Actions */}
      {showActions && (
        <div className="flex justify-end" data-actions onClick={(e) => e.stopPropagation()}>
          <div className="opacity-0 transition-opacity group-hover:opacity-100">
            <DocumentActions
              onDownload={onDownload ?? (() => {})}
              onDelete={onDelete ?? (() => {})}
              showDownload={!!onDownload}
              showDelete={!!onDelete}
              deleteLabel={deleteLabel}
              isRestore={isRestore}
            />
          </div>
        </div>
      )}
    </div>
  );
}
